/**
 * Utility functions for generating IDs, hashes, and other random values
 */

/**
 * Generate a random transaction ID with a given prefix
 */
export function generateTransactionId(prefix: string = "TXN"): string {
  return prefix + Math.random().toString(36).substring(2, 11).toUpperCase();
}

/**
 * Generate a random hash (like Ethereum transaction hash)
 */
export function generateTransactionHash(): string {
  return "0x" + Math.random().toString(16).substring(2, 66);
}

/**
 * Generate a random API key
 */
export function generateApiKey(prefix: string = "api_live"): string {
  const part1 = Math.random().toString(36).substring(2, 15);
  const part2 = Math.random().toString(36).substring(2, 15);
  return `${prefix}_${part1}...${part2}`;
}

/**
 * Generate a random order number
 */
export function generateOrderNumber(): string {
  return generateTransactionId("ORD");
}

/**
 * Generate a random purchase ID
 */
export function generatePurchaseId(): string {
  return generateTransactionId("PUR");
}

/**
 * Generate a random exchange ID
 */
export function generateExchangeId(): string {
  return generateTransactionId("EXC");
}

/**
 * Generate a random transfer ID
 */
export function generateTransferId(): string {
  return generateTransactionId("TRF");
}

/**
 * Generate a random card ID
 */
export function generateCardId(): string {
  return generateTransactionId("CRD");
}

/**
 * Generate a random top-up ID
 */
export function generateTopUpId(): string {
  return generateTransactionId("TUP");
}

/**
 * Format currency amounts consistently
 */
export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format large numbers with appropriate suffixes (K, M, B)
 */
export function formatLargeNumber(num: number): string {
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  }
  if (num >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  }
  if (num >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * Generate a random delay for simulating network requests
 */
export function randomDelay(min: number = 1000, max: number = 3000): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}